// components/radiologyTechnicians/RadiologyTechnicianManager.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Grid,
    Typography,
    Button,
    CircularProgress,
    Chip,
    Paper,
    Stack,
    Autocomplete,
    TextField,
    TablePagination,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Person as PersonIcon,
    FilterList,
    Clear as ClearIcon,
    FileDownload as FileDownloadIcon,
    Description as DescriptionIcon,
    TableChart as TableChartIcon,
    Schedule as ShiftIcon,
    Wc as GenderIcon,
    KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { technicianAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useRTL } from '../../hooks/useRTL';
import NoPrivilege from '../../components/common/NoPrivilege';
import NoContent from '../../components/common/NoContent';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SearchBar from '../../components/common/SearchBar';
import TechnicianCard from './TechnicianCard';
import AddTechnicianDialog from './AddTechnicianDialog';

// Export format options
const EXPORT_FORMATS = {
    simple: {
        key: 'simple',
        labelKey: 'radiologyTechnicians.export.simple',
        defaultLabel: 'Simple Export',
        descriptionKey: 'radiologyTechnicians.export.simpleDesc',
        defaultDescription: 'Basic technician information',
        icon: <DescriptionIcon fontSize="small" />
    },
    detailed: {
        key: 'detailed',
        labelKey: 'radiologyTechnicians.export.detailed',
        defaultLabel: 'Detailed Export',
        descriptionKey: 'radiologyTechnicians.export.detailedDesc',
        defaultDescription: 'Includes certifications and full details',
        icon: <TableChartIcon fontSize="small" />
    },
    byShift: {
        key: 'byShift',
        labelKey: 'radiologyTechnicians.export.byShift',
        defaultLabel: 'Export by Shift',
        descriptionKey: 'radiologyTechnicians.export.byShiftDesc',
        defaultDescription: 'Grouped by work shift',
        icon: <ShiftIcon fontSize="small" />
    },
    byGender: {
        key: 'byGender',
        labelKey: 'radiologyTechnicians.export.byGender',
        defaultLabel: 'Export by Gender',
        descriptionKey: 'radiologyTechnicians.export.byGenderDesc',
        defaultDescription: 'Grouped by gender',
        icon: <GenderIcon fontSize="small" />
    }
};

const RadiologyTechnicianManager = () => {
    const { t } = useTranslation();
    const { user, isSuperAdmin } = useAuth();
    const { isRTL } = useRTL();

    // Filter options
    const SORT_BY_OPTIONS = [
        { value: 'name', label: t('common.name', 'Name') },
        { value: 'age', label: t('radiologyTechnicians.age', 'Age') },
        { value: 'experience', label: t('radiologyTechnicians.experience', 'Experience') },
        { value: 'createdAt', label: t('common.createdAt', 'Created At') },
    ];

    const SORT_ORDER_OPTIONS = [
        { value: 'asc', label: t('common.ascending', 'Ascending') },
        { value: 'desc', label: t('common.descending', 'Descending') },
    ];

    const STATUS_OPTIONS = [
        { value: 'all', label: t('common.all', 'All') },
        { value: 'active', label: t('common.active', 'Active') },
        { value: 'inactive', label: t('common.inactive', 'Inactive') },
    ];

    const GENDER_OPTIONS = [
        { value: '', label: t('common.all', 'All') },
        { value: 'male', label: t('common.male', 'Male') },
        { value: 'female', label: t('common.female', 'Female') },
    ];

    const SHIFT_OPTIONS = [
        { value: '', label: t('common.all', 'All') },
        { value: 'morning', label: t('radiologyTechnicians.shifts.morning', 'Morning') },
        { value: 'evening', label: t('radiologyTechnicians.shifts.evening', 'Evening') },
        { value: 'night', label: t('radiologyTechnicians.shifts.night', 'Night') },
        { value: 'rotating', label: t('radiologyTechnicians.shifts.rotating', 'Rotating') },
    ];

    // States
    const [technicians, setTechnicians] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedTechnician, setSelectedTechnician] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [technicianToDelete, setTechnicianToDelete] = useState(null);
    const [privilegeError, setPrivilegeError] = useState(false);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(12);
    const [total, setTotal] = useState(0);

    // Export states
    const [exporting, setExporting] = useState(false);
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
    const exportMenuOpen = Boolean(exportMenuAnchor);

    // Consolidated filter state
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        gender: '',
        shift: '',
        sortBy: 'name',
        sortOrder: 'asc',
    });

    // Permissions
    const canView = isSuperAdmin || user?.privileges?.some(p => p.module === 'radiologyTechnicians' && p.operations.includes('view'));
    const canAdd = isSuperAdmin || user?.privileges?.some(p => p.module === 'radiologyTechnicians' && p.operations.includes('create'));
    const canEdit = isSuperAdmin || user?.privileges?.some(p => p.module === 'radiologyTechnicians' && p.operations.includes('update'));
    const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'radiologyTechnicians' && p.operations.includes('delete'));
    const canExport = canView;

    // Check if any filter is active
    const hasActiveFilters = filters.search || filters.status !== 'all' || filters.gender || filters.shift;

    // Fetch technicians
    const fetchTechnicians = useCallback(async () => {
        if (!canView) return;

        setLoading(true);
        setPrivilegeError(false);

        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
            };

            if (filters.search) {
                params.search = filters.search;
            }

            if (filters.status !== 'all') {
                params.status = filters.status;
            }

            if (filters.gender) {
                params.gender = filters.gender;
            }

            if (filters.shift) {
                params.shift = filters.shift;
            }

            const response = await technicianAPI.getAll(params);
            setTechnicians(response.data.data || []);
            setTotal(response.data.pagination?.total || 0);
        } catch (err) {
            console.error('Fetch technicians error:', err);
            const backendMessage = err.response?.data?.message;
            if (err.response?.status === 403 || (backendMessage && backendMessage.toLowerCase().includes('privilege'))) {
                setPrivilegeError(true);
            } else {
                toast.error(backendMessage || t('radiologyTechnicians.fetchError', 'Failed to fetch technicians'));
            }
            setTechnicians([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, filters, canView, t]);

    useEffect(() => {
        fetchTechnicians();
    }, [fetchTechnicians]);

    // Filter handlers
    const handleFilterChange = (key, value) => {
        setPage(0);
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleClearFilters = () => {
        setPage(0);
        setFilters({
            search: '',
            status: 'all',
            gender: '',
            shift: '',
            sortBy: 'name',
            sortOrder: 'asc',
        });
    };

    // Export menu handlers
    const handleExportMenuOpen = (event) => {
        setExportMenuAnchor(event.currentTarget);
    };

    const handleExportMenuClose = () => {
        setExportMenuAnchor(null);
    };

    // Export handler
    const handleExport = async (format = 'simple') => {
        handleExportMenuClose();
        setExporting(true);

        try {
            // Build export params from current filters
            const params = {
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
            };

            if (filters.search) params.search = filters.search;
            if (filters.status !== 'all') params.status = filters.status;
            if (filters.gender) params.gender = filters.gender;
            if (filters.shift) params.shift = filters.shift;

            let response;
            let filenamePrefix = 'radiology-technicians';

            // Call appropriate API based on format
            switch (format) {
                case 'detailed':
                    response = await technicianAPI.exportDetailed(params);
                    filenamePrefix = 'radiology-technicians_detailed';
                    break;
                case 'byShift':
                    response = await technicianAPI.exportByShift(params);
                    filenamePrefix = 'radiology-technicians_by_shift';
                    break;
                case 'byGender':
                    response = await technicianAPI.exportByGender(params);
                    filenamePrefix = 'radiology-technicians_by_gender';
                    break;
                case 'simple':
                default:
                    params.exportFormat = 'simple';
                    response = await technicianAPI.export(params);
                    filenamePrefix = 'radiology-technicians';
                    break;
            }

            // Create blob and trigger download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Generate filename with date
            const date = new Date().toISOString().split('T')[0];
            link.download = `${filenamePrefix}_${date}.xlsx`;

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(t('radiologyTechnicians.exportSuccess', 'Technicians exported successfully'));
        } catch (err) {
            console.error('Export error:', err);
            toast.error(
                err.response?.data?.message ||
                t('radiologyTechnicians.exportError', 'Failed to export technicians')
            );
        } finally {
            setExporting(false);
        }
    };

    // Dialog handlers
    const handleOpenDialog = (technician = null) => {
        setSelectedTechnician(technician);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedTechnician(null);
    };

    // Delete handlers
    const handleDeleteClick = (technician) => {
        setTechnicianToDelete(technician);
        setOpenConfirm(true);
    };

    const handleDelete = async () => {
        if (!technicianToDelete) return;

        try {
            await technicianAPI.delete(technicianToDelete._id);
            toast.success(t('radiologyTechnicians.deleteSuccess', 'Technician deleted successfully'));

            // If we deleted the last item on the current page, go to previous page
            if (technicians.length === 1 && page > 0) {
                setPage(page - 1);
            } else {
                fetchTechnicians();
            }
        } catch (err) {
            console.error('Delete error:', err);
            toast.error(err.response?.data?.message || t('radiologyTechnicians.deleteError', 'Failed to delete technician'));
        } finally {
            setOpenConfirm(false);
            setTechnicianToDelete(null);
        }
    };

    // Pagination handlers
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Permission check
    if (!canView || privilegeError) {
        return <NoPrivilege message={t('radiologyTechnicians.noPrivilegeError', 'You do not have permission to view radiology technicians.')} />;
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h4" component="h1">
                        {t('radiologyTechnicians.title', 'Radiology Technicians')}
                    </Typography>
                    {!loading && (
                        <Chip
                            label={`${total} ${t('common.total', 'total')}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>
                <Stack direction="row" spacing={1}>
                    {/* Export Button with Dropdown */}
                    {canExport && (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                                endIcon={<ArrowDownIcon />}
                                onClick={handleExportMenuOpen}
                                disabled={exporting || loading}
                                aria-controls={exportMenuOpen ? 'technician-export-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={exportMenuOpen ? 'true' : undefined}
                            >
                                {exporting
                                    ? t('common.exporting', 'Exporting...')
                                    : t('common.export', 'Export')
                                }
                            </Button>
                            <Menu
                                id="technician-export-menu"
                                anchorEl={exportMenuAnchor}
                                open={exportMenuOpen}
                                onClose={handleExportMenuClose}
                                MenuListProps={{
                                    'aria-labelledby': 'technician-export-button',
                                }}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                {/* Simple Export */}
                                <MenuItem onClick={() => handleExport('simple')}>
                                    <ListItemIcon>
                                        {EXPORT_FORMATS.simple.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t(EXPORT_FORMATS.simple.labelKey, EXPORT_FORMATS.simple.defaultLabel)}
                                        secondary={t(EXPORT_FORMATS.simple.descriptionKey, EXPORT_FORMATS.simple.defaultDescription)}
                                    />
                                </MenuItem>

                                <Divider />

                                {/* Detailed Export */}
                                <MenuItem onClick={() => handleExport('detailed')}>
                                    <ListItemIcon>
                                        {EXPORT_FORMATS.detailed.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t(EXPORT_FORMATS.detailed.labelKey, EXPORT_FORMATS.detailed.defaultLabel)}
                                        secondary={t(EXPORT_FORMATS.detailed.descriptionKey, EXPORT_FORMATS.detailed.defaultDescription)}
                                    />
                                </MenuItem>

                                <Divider />

                                {/* Export by Shift */}
                                <MenuItem onClick={() => handleExport('byShift')}>
                                    <ListItemIcon>
                                        {EXPORT_FORMATS.byShift.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t(EXPORT_FORMATS.byShift.labelKey, EXPORT_FORMATS.byShift.defaultLabel)}
                                        secondary={t(EXPORT_FORMATS.byShift.descriptionKey, EXPORT_FORMATS.byShift.defaultDescription)}
                                    />
                                </MenuItem>

                                <Divider />

                                {/* Export by Gender */}
                                <MenuItem onClick={() => handleExport('byGender')}>
                                    <ListItemIcon>
                                        {EXPORT_FORMATS.byGender.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t(EXPORT_FORMATS.byGender.labelKey, EXPORT_FORMATS.byGender.defaultLabel)}
                                        secondary={t(EXPORT_FORMATS.byGender.descriptionKey, EXPORT_FORMATS.byGender.defaultDescription)}
                                    />
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                    {canAdd && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                        >
                            {t('radiologyTechnicians.addTitle', 'Add Technician')}
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Filters Paper */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack direction="row" alignItems="center" gap={2} mb={2}>
                    <FilterList />
                    <Typography variant="h6">{t('filters', 'Filters')}</Typography>
                    {hasActiveFilters && (
                        <Chip
                            label={t('common.filtersApplied', 'Filters will be applied to export')}
                            size="small"
                            color="info"
                            variant="outlined"
                        />
                    )}
                </Stack>

                {/* Filter Row 1 - Search, Status, Gender */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" mb={2}>
                    <SearchBar
                        onSearch={(query) => handleFilterChange('search', query)}
                        initialValue={filters.search}
                        placeholder={t('radiologyTechnicians.searchPlaceholder', 'Search technicians...')}
                        sx={{ flexGrow: 1, minWidth: '250px' }}
                    />

                    {/* Status Filter */}
                    <Autocomplete
                        options={STATUS_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={STATUS_OPTIONS.find(opt => opt.value === filters.status)}
                        onChange={(event, newValue) => handleFilterChange('status', newValue?.value || 'all')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('common.status', 'Status')} size="small" />
                        )}
                        sx={{ minWidth: 150 }}
                        disableClearable
                    />

                    {/* Gender Filter */}
                    <Autocomplete
                        options={GENDER_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={GENDER_OPTIONS.find(opt => opt.value === filters.gender) || GENDER_OPTIONS[0]}
                        onChange={(event, newValue) => handleFilterChange('gender', newValue?.value || '')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('radiologyTechnicians.gender', 'Gender')} size="small" />
                        )}
                        sx={{ minWidth: 150 }}
                        disableClearable
                    />

                    {/* Shift Filter */}
                    <Autocomplete
                        options={SHIFT_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={SHIFT_OPTIONS.find(opt => opt.value === filters.shift) || SHIFT_OPTIONS[0]}
                        onChange={(event, newValue) => handleFilterChange('shift', newValue?.value || '')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('radiologyTechnicians.shift', 'Shift')} size="small" />
                        )}
                        sx={{ minWidth: 150 }}
                        disableClearable
                    />
                    <Autocomplete
                        options={SORT_BY_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={SORT_BY_OPTIONS.find(opt => opt.value === filters.sortBy)}
                        onChange={(event, newValue) => handleFilterChange('sortBy', newValue?.value || 'name')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('common.sortBy', 'Sort By')} size="small" />
                        )}
                        sx={{ minWidth: 150 }}
                        disableClearable
                    />

                    <Autocomplete
                        options={SORT_ORDER_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={SORT_ORDER_OPTIONS.find(opt => opt.value === filters.sortOrder)}
                        onChange={(event, newValue) => handleFilterChange('sortOrder', newValue?.value || 'asc')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('common.order', 'Order')} size="small" />
                        )}
                        sx={{ minWidth: 150 }}
                        disableClearable
                    />

                </Stack>

                {/* Filter Row 2 - Sort Options */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <Button
                            variant="text"
                            color="inherit"
                            onClick={handleClearFilters}
                            startIcon={<ClearIcon />}
                            size="small"
                        >
                            {t('common.clearFilters', 'Clear')}
                        </Button>
                    )}
                </Stack>
            </Paper>

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    {filters.search && (
                        <Chip
                            label={`${t('common.search', 'Search')}: ${filters.search}`}
                            size="small"
                            onDelete={() => handleFilterChange('search', '')}
                        />
                    )}
                    {filters.status !== 'all' && (
                        <Chip
                            label={`${t('common.status', 'Status')}: ${STATUS_OPTIONS.find(o => o.value === filters.status)?.label}`}
                            size="small"
                            color={filters.status === 'active' ? 'success' : 'default'}
                            onDelete={() => handleFilterChange('status', 'all')}
                        />
                    )}
                    {filters.gender && (
                        <Chip
                            label={`${t('radiologyTechnicians.gender', 'Gender')}: ${GENDER_OPTIONS.find(o => o.value === filters.gender)?.label}`}
                            size="small"
                            color="secondary"
                            onDelete={() => handleFilterChange('gender', '')}
                        />
                    )}
                    {filters.shift && (
                        <Chip
                            label={`${t('radiologyTechnicians.shift', 'Shift')}: ${SHIFT_OPTIONS.find(o => o.value === filters.shift)?.label}`}
                            size="small"
                            color="primary"
                            onDelete={() => handleFilterChange('shift', '')}
                        />
                    )}
                </Box>
            )}

            {/* Results Info */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('common.showing', 'Showing')} {total === 0 ? 0 : Math.min(page * rowsPerPage + 1, total)}-{Math.min((page + 1) * rowsPerPage, total)} {t('common.of', 'of')} {total} {t('common.items', 'items')}
            </Typography>

            {/* Content */}
            {loading ? (
                <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                    <CircularProgress />
                </Paper>
            ) : technicians.length === 0 ? (
                <Paper sx={{ p: 4 }}>
                    <NoContent
                        icon={<PersonIcon sx={{ fontSize: 64 }} />}
                        title={
                            hasActiveFilters
                                ? t('radiologyTechnicians.noFilterResults', 'No technicians match your filters')
                                : t('radiologyTechnicians.noData', 'No Technicians Found')
                        }
                        description={
                            hasActiveFilters
                                ? t('radiologyTechnicians.tryAdjustingFilters', 'Try adjusting your filter criteria')
                                : t('radiologyTechnicians.noDataDescription', 'Start by adding your first radiology technician.')
                        }
                        action={
                            canAdd && !hasActiveFilters && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleOpenDialog()}
                                >
                                    {t('radiologyTechnicians.addTitle', 'Add Technician')}
                                </Button>
                            )
                        }
                    />
                </Paper>
            ) : (
                <>
                    {/* Technician Cards Grid */}
                    <Grid
                        container
                        spacing={3}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        sx={{
                            flexDirection: isRTL ? 'row-reverse' : 'row',
                            justifyContent: isRTL ? 'flex-end' : 'flex-start',
                        }}
                    >
                        {technicians.map((technician) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={technician._id}>
                                <TechnicianCard
                                    technician={technician}
                                    canEdit={canEdit}
                                    canDelete={canDelete}
                                    onEdit={handleOpenDialog}
                                    onDelete={handleDeleteClick}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Pagination */}
                    <Paper sx={{ mt: 3 }}>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={handlePageChange}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[6, 12, 24, 48]}
                            labelRowsPerPage={t('common.rowsPerPage', 'Items per page')}
                        />
                    </Paper>
                </>
            )}

            {/* Dialogs */}
            <AddTechnicianDialog
                open={openDialog}
                onClose={handleCloseDialog}
                selectedTechnician={selectedTechnician}
                onSuccess={fetchTechnicians}
            />

            <ConfirmDialog
                open={openConfirm}
                title={t('radiologyTechnicians.deleteConfirmTitle', 'Delete Technician')}
                message={
                    technicianToDelete
                        ? `${t('radiologyTechnicians.deleteConfirmMessage', 'Are you sure you want to delete')} "${technicianToDelete.name}"?`
                        : t('radiologyTechnicians.deleteConfirmMessage', 'Are you sure you want to delete this technician?')
                }
                onConfirm={handleDelete}
                onClose={() => {
                    setOpenConfirm(false);
                    setTechnicianToDelete(null);
                }}
            />
        </Box>
    );
};

export default RadiologyTechnicianManager;